/**
 * Buyer-side shopping cart service.
 *
 * All mutations flow through SECURITY DEFINER RPCs so the buyer-vs-
 * vendor permission checks + price-snapshot logic + checkout
 * materialization stay in one place. Reads are RLS-scoped: a buyer
 * only ever sees their own carts; the vendor's user can read carts
 * targeted at their vendor for inbox visibility.
 */

import { supabase } from '../client';
import { fireShopAdConversion } from './shopAdsV2';
import type { Json } from '../generated';

export type ShopCartStatus = 'open' | 'submitted' | 'abandoned';

export interface ShopCart {
  id: string;
  buyer_user_id: string;
  vendor_id: string;
  status: ShopCartStatus;
  currency: string;
  submitted_po_id: string | null;
  created_at: string;
  updated_at: string;
  submitted_at: string | null;
  archived_at: string | null;
}

export interface ShopCartLine {
  id: string;
  cart_id: string;
  product_id: string;
  product_name_snapshot: string;
  qty: number;
  unit_price_cents: number;
  currency: string;
  line_notes: string | null;
  added_at: string;
  updated_at: string;
}

/**
 * Returns the buyer's open cart for this vendor, creating one on
 * demand if none exists. Backed by `shop.rpc_buyer_cart_get`.
 */
export async function getOrCreateBuyerCart(vendorId: string): Promise<ShopCart> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_buyer_cart_get', { p_vendor_id: vendorId });
  if (error) throw error;
  return data as ShopCart;
}

export async function listBuyerOpenCarts(): Promise<ShopCart[]> {
  // Returns the SIGNED-IN BUYER's open carts -- nothing else.
  //
  // The `shop.carts` RLS policy set deliberately permits two reader
  // roles: `carts_buyer_all` (the buyer who owns the cart) AND
  // `carts_vendor_read` (any vendor team member of the cart's
  // target vendor, so they can render incoming-cart context in
  // their inbox). A user who is BOTH a buyer AND a vendor team
  // member therefore sees a UNION: their own carts plus every
  // inbound cart aimed at their vendor.
  //
  // For buyer-facing surfaces (`CartIconBadge`, `CartHubPage`,
  // `VendorCartPage`) the inbound rows are noise -- they would
  // surface another buyer's cart as if it were the signed-in
  // user's. We therefore filter on `buyer_user_id = auth.uid()`
  // explicitly. Defense in depth: the function semantics match
  // its name regardless of future RLS changes.
  const supa = supabase();
  const { data: sess } = await supa.auth.getSession();
  const userId = sess?.session?.user.id;
  if (!userId) return [];
  const { data, error } = await (supa as any)
    .schema('shop')
    .from('carts')
    .select('*')
    .eq('buyer_user_id', userId)
    .eq('status', 'open')
    .is('archived_at', null)
    .order('updated_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as ShopCart[];
}

export async function listCartLines(cartId: string): Promise<ShopCartLine[]> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('cart_lines')
    .select('*')
    .eq('cart_id', cartId)
    .order('added_at', { ascending: true })
    .order('id', { ascending: true })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as ShopCartLine[];
}

export async function addBuyerCartLine(input: {
  cartId: string;
  productId: string;
  qty?: number;
  lineNotes?: string;
}): Promise<ShopCartLine> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_buyer_cart_add', {
      p_cart_id: input.cartId,
      p_product_id: input.productId,
      p_qty: input.qty ?? 1,
      p_line_notes: input.lineNotes ?? null,
    });
  if (error) throw error;
  void fireShopAdConversion({
    eventType: 'conversion_cart_add',
    targetKind: 'cart_add',
    targetId: (data as ShopCartLine).id,
  });
  return data as ShopCartLine;
}

export async function updateBuyerCartLineQty(input: {
  lineId: string;
  qty: number;
}): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_buyer_cart_update_line', {
      p_line_id: input.lineId,
      p_qty: input.qty,
    });
  if (error) throw error;
}

export async function removeBuyerCartLine(lineId: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_buyer_cart_remove_line', { p_line_id: lineId });
  if (error) throw error;
}

/**
 * Computes the cart subtotal in cents, locally on the line snapshot.
 * Cart lines store unit_price_cents at add-time, so this is the
 * authoritative number the buyer will see at checkout.
 */
export function computeCartSubtotalCents(lines: ShopCartLine[]): number {
  let total = 0;
  for (const line of lines) {
    total += line.qty * line.unit_price_cents;
  }
  return total;
}

/**
 * Atomic checkout. Materializes the cart into a fresh
 * `shop.purchase_orders` row (status='submitted'), copies cart
 * lines, snapshots the address, flips cart status. Returns the new
 * purchase_order_id.
 *
 * v1 payment compromise: no payment is collected. The vendor receives
 * the same po_submitted email they receive today.
 *
 * Pass `attestationAcknowledged = true` to record the research-use
 * attestation in the same transaction. If attestation is required
 * (fn_buyer_attestation_required) and you pass false, the RPC raises
 * `attestation_required` with errcode `P0001`.
 */
export async function submitBuyerCheckout(input: {
  cartId: string;
  shippingAddressId: string;
  attestationAcknowledged: boolean;
  buyerNotes?: string;
  /**
   * Best-effort fingerprint material for the attestation. Supabase
   * strips the originating IP at the API edge, so the IP here is
   * "what the client knows" -- still useful for UA hashing.
   */
  ip?: string;
  userAgent?: string;
}): Promise<string> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_buyer_checkout_submit', {
      p_cart_id: input.cartId,
      p_shipping_address_id: input.shippingAddressId,
      p_attestation_acknowledged: input.attestationAcknowledged,
      p_buyer_notes: input.buyerNotes ?? null,
      p_ip: input.ip ?? null,
      p_user_agent: input.userAgent ?? null,
    });
  if (error) throw error;
  const poId = data as string;
  // Treat checkout submission as the buyer-intent conversion. Revenue
  // is not yet known (vendor revises pricing on the PO), so we leave
  // target_value_cents null; the conversion charts get the count.
  void fireShopAdConversion({
    eventType: 'conversion_purchase',
    targetKind: 'order',
    targetId: poId,
  });
  return poId;
}

export interface BuyerCartWithLines {
  cart: ShopCart;
  lines: ShopCartLine[];
  subtotalCents: number;
}

export async function getBuyerCartWithLines(vendorId: string): Promise<BuyerCartWithLines> {
  const cart = await getOrCreateBuyerCart(vendorId);
  const lines = await listCartLines(cart.id);
  return {
    cart,
    lines,
    subtotalCents: computeCartSubtotalCents(lines),
  };
}

// Re-export Json so consumers that pass cart metadata don't need a
// second import.
export type { Json };
